import React from 'react';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';

// Faithful placeholder for the SegmentsPage content card during initial
// load. Mirrors the loaded title, filter bar and cards so the page does not reflow
// when data arrives:
//   1. Title row    — "Segments" label + "Create Segment" button
//   2. Filter bar   — filter and export placeholders
//   3. Cards grid   — 6 skeleton cards inside the same outer border
// Shares the continuous-border card frame the real component uses (top
// rounded, bottom rounded, no visual gap between sections).
export function SegmentsSkeleton(): JSX.Element {
  return (
    <div className="flex flex-1 flex-col" role="status" aria-label="Loading segments" aria-busy="true">
      {/* Title row — chips for "Segments" label + "Create Segment" button. */}
      <div className="compact-list-toolbar flex flex-wrap items-center justify-between rounded-t-md border-t border-b border-l border-r border-edge bg-white px-4 py-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-24 rounded bg-paper-inset" />
        </div>
        <Skeleton className="h-[28px] w-32 rounded-md bg-paper-inset" />
      </div>

      {/* Filter / Export bar — mirrors the layout under the title row in
          the loaded state so the page doesn't reflow when data arrives. */}
      <div className="compact-list-toolbar flex flex-row justify-between border-l border-r border-b border-edge bg-white px-4 py-2">
        <div className="flex flex-row gap-2">
          <Skeleton className="h-[28px] w-20 rounded-lg bg-paper-inset" />
        </div>
        <Skeleton className="h-[28px] w-24 rounded-lg bg-paper-inset" />
      </div>

      {/* Cards grid — same outer frame as broadcasts' table body, but
          with 6 card placeholders inside (matches the seeded count).
          `flex-1` so the card stretches to fill the page's vertical
          space when there are only a few segments (matches the broadcasts
          table card behavior). */}
      <div className="flex-1 rounded-b-md border-l border-r border-b border-edge bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div
              key={idx}
              className="flex flex-col gap-2 rounded-md border border-edge bg-paper-light p-4"
            >
              <Skeleton className="h-4 w-1/2 rounded bg-paper-inset" />
              <Skeleton className="h-3 w-full rounded bg-paper-inset" />
              <Skeleton className="h-3 w-3/4 rounded bg-paper-inset" />
              <div className="mt-auto flex items-center justify-between pt-2">
                <Badge variant="neutral">
                  <Skeleton className="h-4 w-16 rounded bg-paper-inset" />
                </Badge>
                <Skeleton className="h-3 w-10 rounded bg-paper-inset" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
